import { useCurrentWorkspace } from '@/context/current-workspace';
import type { SetupGuide } from '@/features/setup-guide';
import { getFirstMessageData, useSetupGuide } from '@/features/setup-guide';
import { fetchAccountById } from '@/services/auth/account-api';
import { AccountFeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';
import { accountStore } from '@/services/auth/store';
import { AccountState, type RawAccount } from '@/services/auth/types';
import { AppBundle } from '@/utils/app-bundles';
import { useSampleWorkspace } from '@/features/workspaces';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation } from '@/services/router/compat';

type ExpandedSetupGuide = SetupGuide & { first_message_sent: boolean; is_in_trial_mode: boolean };
interface UseSetupGuide {
  isLoading: boolean;
  setupGuide: ExpandedSetupGuide;
  shouldHideSetupGuide: boolean;
  shouldHideTrialProgress: boolean;
}

const getTrialProgressSteps: () => (keyof SetupGuide)[] = () => {
  if (hasFeatureFlag(AccountFeatureFlag.TourtipAdventuresV1) === true) {
    return [
      'people_tourtips_explored_at',
      'campaign_tourtips_explored_at',
      'sample_broadcast_explored_at',
      'sample_workflow_explored_at',
    ];
  }
  return ['sample_broadcast_explored_at', 'sample_workflow_explored_at', 'sample_workspace_explored_at'];
};

export default function useSetupGuideExperiment(): UseSetupGuide {
  const location = useLocation();
  const account = useStore(accountStore);
  const previousUrl = useRef(location.pathname);
  const { id, account_id: accountId } = useCurrentWorkspace() ?? {};
  const { isSampleWorkspace } = useSampleWorkspace();

  // Shares `useSetupGuide`'s query rather than opening a second one on the same key. This used to
  // be its own `useQuery` whose `queryFn` swallowed errors into `null`, which meant a failed fetch
  // read as `isError` or as `isSuccess` with no data depending on which hook mounted first.
  //
  // The sample gate travels as `enabled`. That suppresses the request only where this hook is the
  // sole observer: `enabled` is per-observer, so on a sample workspace's home page the home hook
  // mounts the same entry without it and the fetch happens anyway — as it did before. FPS-4144.
  const { setupGuideQuery } = useSetupGuide({ enabled: !isSampleWorkspace(id), keepPreviousWorkspaceData: true });

  const accountQuery = useQuery({
    queryKey: ['subscriptionStatus', accountId],
    enabled: accountId != null && !isSampleWorkspace(id),
    queryFn: async () => {
      if (accountId == null) {
        return Promise.reject();
      }
      return fetchAccountById(accountId);
    },
  });

  const formattedSetupGuide = useFormattedSetupGuide({
    setupGuide: setupGuideQuery.data?.setup_guide ?? null,
    account: accountQuery?.data?.account ?? null,
    workspaceId: id ?? null,
  });

  const shouldHide =
    isSampleWorkspace(id) ||
    formattedSetupGuide.setupGuide?.is_in_trial_mode === false ||
    account?.account_state !== AccountState.Free;

  const isTrialProgressComplete =
    formattedSetupGuide.isLoading === false &&
    getTrialProgressSteps().every((step) => formattedSetupGuide.setupGuide[step] != null);
  const isSetupGuideComplete = formattedSetupGuide.setupGuide?.completed_at != null;

  const shouldHideSetupGuide = shouldHide || isSetupGuideComplete;
  const shouldHideTrialProgress = shouldHide || isTrialProgressComplete;

  // This effect is used to manually refetch the setup guide
  useEffect(() => {
    // If we are not showing any UI that needs the setup guide, we do not need to refetch
    if (shouldHideSetupGuide && shouldHideTrialProgress) {
      return;
    } else if (setupGuideQuery.isStale && previousUrl.current !== location.pathname) {
      /**
       * If the URL has changed we refetch. This is because a step may have been taken that
       * completes a setup guide step. However if the URL did not change, we can assume
       * that the setup guide progress is the same
       * */
      previousUrl.current = location.pathname;
      setupGuideQuery.refetch({ cancelRefetch: false });
    }
  }, [shouldHideSetupGuide, shouldHideTrialProgress, location.pathname, setupGuideQuery.isStale]);

  return {
    isLoading: setupGuideQuery.isFetching || formattedSetupGuide.isLoading,
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    setupGuide: formattedSetupGuide.setupGuide as ExpandedSetupGuide,
    shouldHideSetupGuide,
    shouldHideTrialProgress,
  };
}

function useFormattedSetupGuide({
  setupGuide,
  account,
  workspaceId,
}: {
  setupGuide: SetupGuide | null;
  account: RawAccount | null;
  workspaceId: number | null;
}) {
  const firstMessageData = useQuery({
    queryKey: ['firstMessageData', workspaceId, setupGuide?.first_message_id, setupGuide?.first_message_type],
    enabled:
      setupGuide != null &&
      workspaceId != null &&
      setupGuide?.first_message_id != null &&
      setupGuide?.first_message_type != null,
    queryFn: async () => {
      if (workspaceId == null || setupGuide?.first_message_id == null || setupGuide?.first_message_type == null) {
        return Promise.reject();
      }
      return await getFirstMessageData(workspaceId, setupGuide.first_message_id, setupGuide.first_message_type);
    },
  });

  /* There is a case of when the journeys account is in trial mode because user signed up for trial from the cdp,
   * The way to determine this is a workaround based on checking if messaging exists in the gated features
   * with the is_trial property set to true.
   * More info about the workaround on UI: https://github.com/customerio/ui/blob/main/app/services/account.ts#L100
   */
  const hasActivatedJourneys = (account?.product_activations ?? []).includes(AppBundle.Journeys);

  if (account != null && account.account_state !== AccountState.Free) {
    // this could be an account that checked out journeys from cdp so let's establish that
    if (hasActivatedJourneys === true && account.gated_features?.messaging !== undefined) {
      // check if messaging is in trial mode
      // The messaging feature may include an is_trial property beyond the standard PlanFeatureLimit
      type MessagingFeature = { is_trial?: boolean; limit?: number };
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      const messagingFeature = account.gated_features.messaging as MessagingFeature;
      const is_trial = messagingFeature.is_trial;

      if (is_trial === undefined) {
        return { isLoading: false, setupGuide: { ...setupGuide, is_in_trial_mode: false, first_message_sent: false } };
      }
    } else {
      return { isLoading: false, setupGuide: { ...setupGuide, is_in_trial_mode: false, first_message_sent: false } };
    }
  }

  if (setupGuide?.first_message_id != null && setupGuide?.first_message_type != null) {
    if (firstMessageData.isLoading) {
      return {
        isLoading: true,
        setupGuide: { ...setupGuide, is_in_trial_mode: true, first_message_sent: false },
      };
    }

    // first meessage to be sent can either be campaign or newsletter. For each one, verify if it has been sent
    if (setupGuide.first_message_type === 'campaign') {
      const campaignState: string | null = firstMessageData?.data?.campaign?.state ?? null;
      return {
        isLoading: false,
        setupGuide: {
          ...setupGuide,
          first_message_sent: typeof campaignState === 'string' ? campaignState !== 'draft' : false,
          is_in_trial_mode: true,
        },
      };
    }
    if (setupGuide.first_message_type === 'newsletter') {
      return {
        isLoading: false,
        setupGuide: {
          ...setupGuide,
          first_message_sent: (firstMessageData?.data?.newsletter?.sent_at ?? 0) > 0,
          is_in_trial_mode: true,
        },
      };
    }
  }
  return { isLoading: false, setupGuide: { ...setupGuide, is_in_trial_mode: true, first_message_sent: false } };
}
